/**
 * App.tsx — Thin root component.
 *
 * All routing, navigation logic, modals, and boot pipeline now live in
 * src/router/AppRouter.tsx. App.tsx exists only to mount the boot-time
 * push notification listener (Capacitor) and render AppRouter.
 *
 * The boot pipeline (auth check → profile check → redirect) is handled
 * by ProtectedRoute / PublicOnlyRoute guards inside AppRouter.
 */

import React, { useEffect, useRef } from 'react';
import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { useNavigate } from 'react-router-dom';
import { NotificationService, YapService } from './services/api';
import { NotificationServiceWeb } from './services/NotificationServiceWeb';
import { useUserData } from './components/UserDataContext';
import { YapDayProvider } from './contexts/YapDayContext';
import { ImageViewerProvider } from './components/ui/ImageViewerProvider';
import { toast } from 'sonner';
import { Bell } from 'lucide-react';
import { PATHS, buildPath } from './router/paths';
import AppRouter from './router/AppRouter';

// Destination for "Update available / Tap to update" pushes. Kept in sync with
// APP_STORE_URL in functions/index.js (sendIosUpdateNotification); used as the
// fallback when a push arrives without its deepLink payload.
const APP_STORE_URL = 'https://apps.apple.com/us/app/afroyap/id6757343172';

export default function App() {
  const navigate = useNavigate();
  const { userProfile, authLoading } = useUserData();
  const pendingNativeTokenRef = useRef<string | null>(null);
  const lastRegisteredIdRef   = useRef<string | number | null>(null);
  const userProfileRef = useRef(userProfile);
  useEffect(() => { userProfileRef.current = userProfile; }, [userProfile]);

  // ── Web push foreground messages ─────────────────────────────────────────────
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    const unsub = NotificationServiceWeb.onForegroundMessage((payload) => {
      if (payload.notification) {
        toast(payload.notification.title || 'Notification', {
          description: payload.notification.body,
          icon: <Bell className="w-4 h-4 text-[#F39C12]" />,
          duration: 6000,
        });
      }
    });
    return () => { if (unsub) unsub(); };
  }, []);

  // ── Service-worker push-click → in-app navigation ────────────────────────────
  // When the user taps a web push notification, the SW posts a PUSH_NAV message
  // with the resolved URL. Use react-router to navigate instead of doing a hard
  // page reload (preserves auth state, scroll position, modal context, etc.).
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    const handler = (event: MessageEvent) => {
      const msg = event.data;
      if (!msg || msg.type !== 'PUSH_NAV' || typeof msg.url !== 'string') return;
      try {
        const parsed = new URL(msg.url);
        // Same-origin only — defense in depth.
        if (parsed.origin !== window.location.origin) return;
        // Suppress the guidelines popup for this session (web path) — mirrors
        // the native pushNotificationActionPerformed listener. Coming from a
        // notification implies the user has already used the app at least once,
        // so reshowing the rules is just friction.
        try { sessionStorage.setItem('enteredFromPush_v1', 'true'); } catch (_) {}
        try { window.dispatchEvent(new CustomEvent('enteredFromPush')); } catch (_) {}
        const dest = parsed.pathname + parsed.search + parsed.hash;
        // Persist so AppRouter replays it once auth settles (cold-start safety net).
        try { sessionStorage.setItem('pendingPushRoute_v1', JSON.stringify({ path: dest, ts: Date.now() })); } catch (_) {}
        navigate(dest);
      } catch (e) {
        console.warn('[Push] PUSH_NAV navigation failed', e);
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [navigate]);

  // ── Clear local notifications left over from older builds ────────────────────
  //
  // Earlier versions scheduled "Golden Hour" and "New Yap Day" as repeating
  // LOCAL notifications on the device. Both broadcasts have since been retired —
  // the Golden Hour cron was removed, and yapDayResetCron was deleted once the
  // feed stopped resetting — but anything already queued on a device keeps
  // firing on its own schedule. Deleting a server function cannot retract it,
  // which is why those notifications outlived the code that created them.
  //
  // Nothing in the app schedules local notifications any more, so cancelling
  // everything pending is safe and self-limiting: on a device with none queued
  // this is a no-op.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    (async () => {
      try {
        const { LocalNotifications } = await import('@capacitor/local-notifications');
        const pending = await LocalNotifications.getPending();
        if (pending.notifications?.length) {
          await LocalNotifications.cancel({ notifications: pending.notifications });
          console.info(`[Boot] Cancelled ${pending.notifications.length} stale local notification(s)`);
        }
      } catch (e) {
        // Plugin unavailable or permission not granted — nothing to clean up.
        console.warn('[Boot] Local notification cleanup skipped:', e);
      }
    })();
  }, []);

  // ── Consolidated push token registration ─────────────────────────────────────
  useEffect(() => {
    if (!userProfile?.id || authLoading) return;
    if (lastRegisteredIdRef.current === userProfile.id) return;

    const registerToken = async () => {
      try {
        const idToRegister = String(userProfile.id);
        if (Capacitor.isNativePlatform()) {
          if (pendingNativeTokenRef.current) {
            await NotificationService.registerDeviceToken(idToRegister, pendingNativeTokenRef.current, Capacitor.getPlatform() as any);
            pendingNativeTokenRef.current = null;
            lastRegisteredIdRef.current = userProfile.id;
            // Subscribe to the broadcast topic. The YapDay and Golden Hour crons that
            // used to send here are both gone; what still uses it is onYapPinned,
            // which broadcasts when a Legion pins a yap to the feed.
            try { await FirebaseMessaging.subscribeToTopic({ topic: 'all_users' }); } catch (_) {}
          } else {
            await PushNotifications.register();
          }
        } else {
          await NotificationServiceWeb.requestAndRegisterToken(idToRegister);
          lastRegisteredIdRef.current = userProfile.id;
        }
      } catch (err) {
        console.warn('[Push] Registration attempt failed:', err);
      }
    };

    const timer = setTimeout(registerToken, 3000);
    return () => clearTimeout(timer);
  }, [userProfile?.id, authLoading]);

  // ── Capacitor push listener setup ────────────────────────────────────────────
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const init = async () => {
      try {
        PushNotifications.addListener('registration', async () => {
          try {
            const { token } = await FirebaseMessaging.getToken();
            const pid = userProfileRef.current?.id;
            if (pid) {
              await NotificationService.registerDeviceToken(String(pid), token, Capacitor.getPlatform() as any);
              lastRegisteredIdRef.current = pid;
              // Subscribe to the broadcast topic. The YapDay and Golden Hour crons that
            // used to send here are both gone; what still uses it is onYapPinned,
            // which broadcasts when a Legion pins a yap to the feed.
              try { await FirebaseMessaging.subscribeToTopic({ topic: 'all_users' }); } catch (_) {}
            } else {
              pendingNativeTokenRef.current = token;
            }
          } catch (e) {
            console.error('[Push] FCM token error:', e);
          }
        });

        PushNotifications.addListener('registrationError', (err) => {
          console.error('[Push] Registration error:', JSON.stringify(err));
        });

        const perm = await PushNotifications.checkPermissions();
        if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
          const newPerm = await PushNotifications.requestPermissions();
          if (newPerm.receive === 'denied') return;
          // Register immediately after user grants
          await PushNotifications.register();
        } else if (perm.receive === 'granted') {
          // Already granted — re-register every app launch so token stays fresh
          await PushNotifications.register();
        }

        if (Capacitor.getPlatform() === 'android') {
          await PushNotifications.createChannel({ id: 'moderation', name: 'Moderation', description: 'Crucial moderation and safety alerts', importance: 5, visibility: 1 });
          await PushNotifications.createChannel({ id: 'interactions', name: 'Interactions', description: 'Social networking interactions', importance: 4, visibility: 1 });
        }

        // Show toast for foreground notifications (iOS suppresses banners when app is active)
        PushNotifications.addListener('pushNotificationReceived', (notification) => {
          const title = notification.title || 'AfroYap';
          const body  = notification.body  || '';
          toast(title, {
            description: body,
            icon: <Bell className="w-4 h-4 text-[#4478ea]" />,
            duration: 6000,
          });
        });

        PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
          const data = notification.notification.data || {};
          const { type, senderId, targetId, deepLink } = data;

          console.log('[Push] Tap →', { type, senderId, targetId, deepLink });

          // App-update prompts point at the App Store, not an in-app screen —
          // open the link externally and stop. Without this the tap fell through
          // to resolveByDeepLink() and just opened the Notifications list.
          const isExternalLink = typeof deepLink === 'string'
            && /^https?:\/\//i.test(deepLink)
            && !deepLink.startsWith(window.location.origin);
          if (type === 'APP_UPDATE' || isExternalLink) {
            const url = isExternalLink ? deepLink : APP_STORE_URL;
            try { window.open(url, '_blank'); } catch (e) { console.warn('[Push] External link open failed', e); }
            return;
          }

          // Suppress the community-guidelines modal for this session — the user
          // is jumping into a specific piece of content from a push, which
          // implies they've used the app already (you don't get pushes before
          // signup). The AppRouter guidelines gate reads this flag and skips
          // the popup when it's set. Cold boots without a push tap still see
          // the modal as before. We also fire an event so AppRouter can dismiss
          // the modal even if it already opened before this callback ran (the
          // native tap callback can arrive after the gate's initial timer).
          try { sessionStorage.setItem('enteredFromPush_v1', 'true'); } catch (_) {}
          try { window.dispatchEvent(new CustomEvent('enteredFromPush')); } catch (_) {}

          // Resolve a single target route string for this notification. We
          // compute one string (rather than navigating inline in each branch)
          // so we can BOTH navigate immediately AND persist the destination —
          // the persisted route is replayed by AppRouter once auth settles, so
          // a cold-start tap whose immediate navigate raced the boot still lands
          // on the right screen instead of a blank/wrong one.
          const resolveByDeepLink = (): string => {
            if (!deepLink)                          return PATHS.NOTIFICATIONS;
            if      (deepLink.includes('chat/'))    return senderId ? `/messages/${senderId}` : PATHS.CHAT;
            else if (deepLink.includes('forum/'))   return `/forum/${deepLink.split('forum/')[1]}`;
            else if (deepLink.includes('tribe/'))   return `/tribe/${deepLink.split('tribe/')[1]}`;
            else if (deepLink.includes('yap/'))     return `/yap/${deepLink.split('yap/')[1]}`;
            else if (deepLink.includes('profile/')) return `/profile/${deepLink.split('profile/')[1]}`;
            else if (deepLink.includes('yaptab'))   return PATHS.YAP_TAB;
            return PATHS.NOTIFICATIONS;
          };

          let target: string;
          if (!type) {
            // Missing type (old payloads / OS-stripped data) — fall back to deepLink.
            target = resolveByDeepLink();
          } else if (type.startsWith('ROOM_')) {
            // A room notification opens the room, matching how a DM notification
            // opens the conversation rather than the chat list.
            const roomId = data.roomId || data.targetId;
            target = roomId ? `/room/${roomId}` : PATHS.CHAT;
            // A join request is a decision waiting on the host, so it lands on
            // the approval list rather than merely inside the room. RoomScreen
            // reads this once its staff status resolves and then clears it.
            if (roomId && type === 'ROOM_JOIN_REQUEST') {
              try { sessionStorage.setItem('open_room_requests_for', String(roomId)); } catch { /* storage unavailable */ }
            }
          } else if (type === 'DIRECT_MESSAGE' || type === 'MESSAGE' || type === 'CHAT_MESSAGE') {
            // Conversation lives at /messages/:userId (the other user's uid),
            // not /chat (the chat-list) nor the composite chatId.
            target = senderId ? `/messages/${senderId}` : PATHS.CHAT;
          } else if (type === 'SLAP' && senderId) {
            target = `/profile/${senderId}`;
          } else if (['CONNECTION_REQUEST','CONNECTION_ACCEPTED','FRIEND_REQUEST','FRIEND_ACCEPTED','friend_request','friend_accepted'].includes(type)) {
            // Show exactly who added / accepted you — open their profile. Only
            // fall back to the notifications list if the sender id is missing.
            target = senderId ? `/profile/${senderId}` : PATHS.NOTIFICATIONS;
          } else if (type === 'WATCHER_MOD') {
            target = PATHS.NOTIFICATIONS;
          } else if ((type.startsWith('FORUM_') || type.startsWith('TRIBE_')) && targetId) {
            // Membership/group-home notifications: targetId is the GROUP id.
            const isGroupHomeType =
              type.includes('JOIN_') ||
              type.includes('INVITE') ||
              type.includes('NEW_MEMBER') ||
              type.includes('CREATED');
            if (isGroupHomeType) {
              target = type.startsWith('FORUM_') ? `/forum/${targetId}` : `/tribe/${targetId}`;
            } else {
              // Activity notifications (COMMENT, LIKE, MENTION, SHOUT, NEW_POST…):
              // targetId is the POST id. YapDetailScreen loads group posts too.
              target = `/yap/${targetId}`;
            }
          } else if (['REPLY_LIKE','reply_like'].includes(type) && (data.yapId || data.postId)) {
            // A reply like targets the REPLY, so its yapId is what opens — using
            // targetId here would route to a post id that is really a reply's.
            target = `/yap/${data.yapId || data.postId}`;
          } else if (['NEW_COMMENT','yap_comment','YAP_COMMENT','yap_like','YAP_LIKE','yap_shout','YAP_SHOUT','MENTION','legion_pin'].includes(type) && targetId) {
            target = `/yap/${targetId}`;
          } else if (['GOLDEN_HOUR','YAPTAB_RESET'].includes(type)) {
            target = PATHS.YAP_TAB;
          } else if (type === 'YAPSTATS') {
            // The daily recap lands on YapTab showing the busiest yaps of the
            // last 24 hours. A query param rather than a persisted sort: the
            // reader asked for this view once, from a notification, and should
            // not find their feed permanently re-sorted afterwards.
            target = `${PATHS.YAP_TAB}?sort=most-replied&window=24h`;
          } else {
            target = resolveByDeepLink();
          }

          // Persist so AppRouter can replay it once the app is ready (covers the
          // cold-start case where this immediate navigate is dropped), then
          // navigate now for the warm case.
          try { sessionStorage.setItem('pendingPushRoute_v1', JSON.stringify({ path: target, ts: Date.now() })); } catch (_) {}
          navigate(target);
        });
      } catch (e) {
        console.warn('[App] Push notification init failed', e);
      }
    };

    init();
    return () => { PushNotifications.removeAllListeners(); };
  }, []);

  return (
    <ImageViewerProvider>
      <YapDayProvider>
        <AppRouter />
      </YapDayProvider>
    </ImageViewerProvider>
  );
}
